import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Player } from '../audio/player';
import { setTimbre, Timbre } from '../audio/synth';

/* ---------- Preferences (per viewer, best effort) ---------- */

export type Depth = 0 | 1 | 2 | 3 | 4;
export type Lens = 'roman' | 'figured';

export interface Prefs {
  depth: Depth;
  lens: Lens;
  timbre: Timbre;
  tempo: number;
  /** Show the moving score in the live dock of a piece. */
  score: boolean;
}

export const DEFAULT_PREFS: Prefs = { depth: 1, lens: 'roman', timbre: 'piano', tempo: 66, score: true };
const STORAGE_KEY = 'gravity-atlas:prefs:v1';

export function loadPrefs(): Prefs {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...DEFAULT_PREFS, ...JSON.parse(raw) };
  } catch {
    /* storage unavailable */
  }
  return DEFAULT_PREFS;
}

export function savePrefs(p: Prefs): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
  } catch {
    /* storage unavailable */
  }
}

export const PrefsContext = createContext<{ prefs: Prefs; setPrefs: (p: Partial<Prefs>) => void }>({
  prefs: DEFAULT_PREFS,
  setPrefs: () => undefined,
});

export function usePrefs() {
  return useContext(PrefsContext);
}

export function usePrefsState() {
  const [prefs, set] = useState<Prefs>(() => loadPrefs());
  const setPrefs = useCallback((p: Partial<Prefs>) => {
    set((old) => {
      const next = { ...old, ...p };
      savePrefs(next);
      return next;
    });
  }, []);
  useEffect(() => setTimbre(prefs.timbre), [prefs.timbre]);
  return useMemo(() => ({ prefs, setPrefs }), [prefs, setPrefs]);
}

/* ---------- Hash routing (plain tokens only: letters, digits, dot, dash) ---------- */

export interface Route {
  view: 'home' | 'piece' | 'library' | 'passion' | 'tunes' | 'tune' | 'forms' | 'form' | 'returns' | 'variations' | 'compare' | 'train' | 'keys' | 'observatory' | 'paths' | 'path' | 'feel' | 'walk' | 'method';
  id?: string;
  sub?: string;
}

export function parseRoute(hash: string): Route {
  const token = hash.replace(/^#/, '');
  if (!token || token === 'atlas') return { view: 'home' };
  const [head, ...rest] = token.split('.');
  if (head === 'paths') return { view: 'paths' };
  if (head === 'library') return { view: 'library' };
  if (head === 'passion') return { view: 'passion' };
  if (head === 'observatory') return { view: 'observatory' };
  if (head === 'tunes') return { view: 'tunes' };
  if (head === 'forms') return { view: 'forms' };
  if (head === 'variations') return { view: 'variations' };
  if (head === 'compare') return { view: 'compare' };
  if (head === 'train') return { view: 'train' };
  if (head === 'keys') return { view: 'keys' };
  if (head === 'returns') return { view: 'returns', id: rest[0] };
  if (head === 'form') return { view: 'form', id: rest[0] };
  if (head === 'tune') return { view: 'tune', id: rest[0], sub: rest[1] };
  if (head === 'path') return { view: 'path', id: rest.join('.') };
  if (head === 'feel') return { view: 'feel', id: rest[0] };
  if (head === 'walk') return { view: 'walk' };
  if (head === 'method') return { view: 'method' };
  return { view: 'piece', id: head, sub: rest[0] };
}

export function useRoute(): Route {
  const [hash, setHash] = useState(() => window.location.hash);
  useEffect(() => {
    const on = () => {
      setHash(window.location.hash);
      window.scrollTo({ top: 0 });
    };
    window.addEventListener('hashchange', on);
    return () => window.removeEventListener('hashchange', on);
  }, []);
  return useMemo(() => parseRoute(hash), [hash]);
}

export function go(token: string): void {
  window.location.hash = token;
}

/* ---------- Player ---------- */

export function usePlayer() {
  const ref = useRef<Player | null>(null);
  if (!ref.current) ref.current = new Player();
  const player = ref.current;
  const [state, setState] = useState({ position: 0, playing: false });
  useEffect(() => {
    let last = -1;
    let lastPlaying = false;
    let lastTime = 0;
    const unsub = player.subscribe((position, playing) => {
      const now = performance.now();
      const moved = Math.abs(position - last) > 0.01;
      if (playing !== lastPlaying || (moved && (!playing || now - lastTime > 32))) {
        last = position;
        lastPlaying = playing;
        lastTime = now;
        setState({ position, playing });
      }
    });
    return () => {
      unsub();
      player.dispose();
    };
  }, [player]);
  return { player, ...state };
}

/* ---------- Layout ---------- */

export function useWidth<T extends HTMLElement>(): [React.RefObject<T | null>, number] {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.getBoundingClientRect().width);
    const ro = new ResizeObserver((entries) => setWidth(entries[0].contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!mq) return;
    const on = () => setReduced(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return reduced;
}

/** Page shortcuts stay quiet while the user types, holds a modifier, or has a dialog (search, settings) open. */
export function shortcutBlocked(e: KeyboardEvent): boolean {
  const el = e.target as HTMLElement | null;
  const tag = el?.tagName ?? '';
  return (
    tag === 'INPUT' ||
    tag === 'SELECT' ||
    tag === 'TEXTAREA' ||
    !!el?.isContentEditable ||
    e.metaKey ||
    e.ctrlKey ||
    e.altKey ||
    e.defaultPrevented ||
    !!document.querySelector('[role="dialog"]')
  );
}
